import type { FunctionComponent } from 'react'
import React, { useContext as useContextCore } from 'react'
import type { Product } from '@ztechapps/common.contract'
import type { SaveCancelButtonsProps } from '@ztechapps/webui.button'
import { FormSaveCancelButtons } from '@ztechapps/webui.form'
import merge from 'lodash/merge'
import { WorkOrderAddFormInput } from './WorkOrderAddFormInput'
import { useWorkOrderAdd } from './useWorkOrderAdd'

export type WorkOrderAddFormValues = {
  product: Product
  quantity: number
}

export type WorkOrderAddComponent = {
  formInput: FunctionComponent
  saveCancelButtons: FunctionComponent<SaveCancelButtonsProps>
}

const defaultComponents: WorkOrderAddComponent = {
  formInput: WorkOrderAddFormInput,
  saveCancelButtons: FormSaveCancelButtons,
}

export type WokOrderAddProviderProps = {
  onSubmit?: (values: WorkOrderAddFormValues) => Promise<void>
  onReset?: () => void
  children: React.ReactNode
  initialValues?: WorkOrderAddFormValues
  components?: WorkOrderAddComponent
}

export type WorkOrderAddContext = {
  validate: (value: WorkOrderAddFormValues) => any
} & Omit<WokOrderAddProviderProps, 'children'>

const context = React.createContext<WorkOrderAddContext>({} as WorkOrderAddContext)
const CoreProvider = context.Provider

export const WorkOrderAddProvider = ({
  children,
  components,
  onSubmit,
  initialValues,
  ...props
}: WokOrderAddProviderProps) => {
  initialValues = initialValues ?? { product: null, quantity: 1 }

  const { onSubmit: defaulOnSubmit } = useWorkOrderAdd()

  onSubmit = onSubmit ?? defaulOnSubmit

  //merge the components with the default components
  const mergedComponents = merge(defaultComponents, components)

  const validate = (value: WorkOrderAddFormValues) => {
    const errors: any = {}
    if (!value?.product?._id) {
      errors.product = 'Required'
    }
    if (!value.quantity) {
      errors.quantity = 'Required'
    } else {
      if (value.quantity <= 0) {
        errors.quantity = 'Must be greater than zero.'
      }
    }

    return errors
  }

  return (
    <CoreProvider value={{ initialValues, validate, components: mergedComponents, onSubmit, ...props }}>
      {children}
    </CoreProvider>
  )
}

export const useWorkOrderAddContext = () => useContextCore(context)
